"use client";

import { type ReactNode } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  Tabs,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { CandidateInfo } from "./CandidateInfo";
import { HiDocumentAdd } from "react-icons/hi";

interface ResultTab {
  id: string;
  label: string;
  icon: ReactNode;
}

interface ResultHeaderProps {
  candidateName: string;
  candidateTitle: string;
  tabs: readonly ResultTab[];
  currentIndex: number;
  onReset: () => void;
}

export function ResultHeader({
  candidateName,
  candidateTitle,
  tabs,
  currentIndex,
  onReset,
}: ResultHeaderProps) {
  const t = useTranslations("resume_benchmark");

  return (
    <Box
      position="sticky"
      top="4.125rem"
      zIndex={100}
      bg="blackAlpha.500"
      backdropFilter="blur(20px)"
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
    >
      <Container maxW="1200px" px={{ base: 4, md: 8 }}>
        <Flex align="center" justify="space-between" py={3} gap={4}>
          <CandidateInfo name={candidateName} title={candidateTitle} />

          <Tabs.List
            gap={1}
            bg="whiteAlpha.50"
            rounded="full"
            p={1}
            flexGrow={1}
            justifyContent="center"
            maxW="fit-content"
            mx="auto"
          >
            {tabs.map((tab) => (
              <Tabs.Trigger
                key={tab.id}
                value={tab.id}
                rounded="full"
                px={4}
                fontSize="xs"
                color="whiteAlpha.500"
                fontWeight="normal"
                gap={2}
                transition="all 0.2s"
                _selected={{
                  bg: "indigo.500",
                  color: "white",
                  fontWeight: "bold",
                }}
                _hover={{
                  color: "white",
                  bg: "whiteAlpha.100",
                  _selected: { bg: "indigo.400" },
                }}
              >
                {tab.icon}
                {tab.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <HStack gap={1} flexShrink={0}>
            <Button
              size="sm"
              variant="ghost"
              rounded="lg"
              color="neutral.200"
              bg="whiteAlpha.50"
              fontSize="xs"
              _hover={{ color: "white", bg: "primary.700" }}
              onClick={onReset}
            >
              {t("result.new")}
              <Icon as={HiDocumentAdd} color="inherit" />
            </Button>
          </HStack>
        </Flex>

        <Box h="4px" bg="blackAlpha.100" rounded="full">
          <Box
            h="full"
            bg="primary.500"
            rounded="full"
            transition="width 0.35s ease"
            w={`${((currentIndex + 1) / tabs.length) * 100}%`}
          />
        </Box>
      </Container>
    </Box>
  );
}
